/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react"
import {
  Box,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material"
import { collection, onSnapshot, QuerySnapshot } from "firebase/firestore"
import { db } from "../../firebase"
import { tokens } from "../../theme"
import { useNavigate } from "react-router-dom"
import AddIcon from "@mui/icons-material/Add"
import dayjs from "dayjs"

const MONTHS = [
  { value: "04", label: "4月" },
  { value: "05", label: "5月" },
  { value: "06", label: "6月" },
  { value: "07", label: "7月" },
  { value: "08", label: "8月" },
  { value: "09", label: "9月" },
  { value: "10", label: "10月" },
  { value: "11", label: "11月" },
  { value: "12", label: "12月" },
  { value: "01", label: "1月" },
  { value: "02", label: "2月" },
  { value: "03", label: "3月" },
]

interface PestControlData {
  id?: string
  year: string
  month: string
  manager: string
  verifier: string
  records: Array<{
    no: string
    implementationDate: string
    implementer: string
    inspector: string
    traps: Record<string, number>
  }>
  remarks: string
  createdAt?: string
  updatedAt?: string
}

const PestControlManagement = () => {
  const theme = useTheme()
  const colors = tokens(theme.palette.mode)
  const navigate = useNavigate()
  const [allRecords, setAllRecords] = useState<PestControlData[]>([])
  const [filteredRecords, setFilteredRecords] = useState<PestControlData[]>([])
  const [selectedYear, setSelectedYear] = useState<string>(dayjs().format("YYYY"))
  const [availableYears, setAvailableYears] = useState<string[]>([])

  useEffect(() => {
    const pestControlRef = collection(db, "pestControlManagements")
    const unsubscribe = onSnapshot(pestControlRef, (snapshot: QuerySnapshot) => {
      const records = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as PestControlData))

      setAllRecords(records)

      // Extract unique years and sort them
      const years = [...new Set(records.map(r => r.year))].sort((a, b) => b.localeCompare(a))
      setAvailableYears(years)

      // If current year is not in the list, add it
      if (!years.includes(dayjs().format("YYYY"))) {
        setAvailableYears([dayjs().format("YYYY"), ...years])
      }
    })
    return unsubscribe
  }, [])

  useEffect(() => {
    // Filter records by selected year
    const filtered = allRecords
      .filter(record => record.year === selectedYear)
      .sort((a, b) => {
        const dateA = `${a.year}-${a.month}`
        const dateB = `${b.year}-${b.month}`
        return dateB.localeCompare(dateA)
      })
    setFilteredRecords(filtered)
  }, [selectedYear, allRecords])

  const getMonthLabel = (monthValue: string) => {
    const monthObj = MONTHS.find(m => m.value === monthValue)
    return monthObj ? monthObj.label : monthValue
  }

  const handleNew = () => {
    navigate('/admin/pest-control-management/new')
  }

  const handleEdit = (recordId: string) => {
    navigate(`/admin/pest-control-management/${recordId}`)
  }

  return (
    <Box m={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          そ族昆虫防除記録
        </Typography>
        <Box display="flex" gap={2} alignItems="center">
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>年度</InputLabel>
            <Select
              value={selectedYear}
              label="年度"
              onChange={(e) => setSelectedYear(e.target.value)}
            >
              {availableYears.map((year) => (
                <MenuItem key={year} value={year}>
                  {year}年度
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleNew}
          >
            新規作成
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: colors.blueAccent[700] }}>
              <TableCell sx={{ fontWeight: "bold" }}>年度</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>対象月</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>担当責任者</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>確認者</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>更新日</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRecords.map((record) => (
              <TableRow key={record.id} hover>
                <TableCell>{record.year}年度</TableCell>
                <TableCell>{getMonthLabel(record.month)}</TableCell>
                <TableCell>{record.manager}</TableCell>
                <TableCell>{record.verifier}</TableCell>
                <TableCell>{record.updatedAt}</TableCell>
                <TableCell>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleEdit(record.id!)}
                  >
                    編集
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {filteredRecords.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  {selectedYear}年度のデータがありません
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}

export default PestControlManagement
